import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';
import { type FlattenedWorkspaceAgentAssetPillItem } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { type WorkspaceAgentPillClip } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentPillClipsById';
import { truncateWorkspaceAgentId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/truncateWorkspaceAgentId';

type ClipLookupFailed = boolean | ((clipId: string) => boolean);

const clipLookupFailedFor = (clipLookupFailed: ClipLookupFailed, clipId: string | undefined): boolean => {
  if (!clipId) {
    return false;
  }

  return typeof clipLookupFailed === 'function' ? clipLookupFailed(clipId) : clipLookupFailed;
};

/**
 * Unavailable members keep their place in the row; `isResolving` stops every list flashing "Unavailable
 * asset" on its way there. A member with no clip id was never resolvable, so it reads as neutral.
 * A failed lookup is per id: an id a successful response already omitted stays unavailable.
 */
export const toWorkspaceAgentAssetListThumbnails = (
  items: FlattenedWorkspaceAgentAssetPillItem[],
  resolveClip: (item: FlattenedWorkspaceAgentAssetPillItem) => WorkspaceAgentPillClip | undefined,
  isResolving: boolean,
  /** A failed `Clips.listByIds` is not "these assets are gone". The list chip still renders. */
  clipLookupFailed: ClipLookupFailed,
): WorkspaceAgentThumbnail[] =>
  items.map((item, index) => {
    const clip = resolveClip(item);
    const lookupFailed = clipLookupFailedFor(clipLookupFailed, item.clipId);
    const isUnavailable = !!item.clipId && !clip && !isResolving && !lookupFailed;

    return {
      canOpen: !!item.clipId && (!!clip || (isResolving && !lookupFailed)),
      ext: clip?.ext ?? null,
      id: item.clipId ?? `${item.assetId}-${index}`,
      isUnavailable,
      height: clip?.height ?? null,
      label: clip?.name ?? (isUnavailable ? 'Unavailable asset' : `Asset ${truncateWorkspaceAgentId(item.assetId)}`),
      thumbnailUrl: clip?.thumbnailUrl ?? null,
      width: clip?.width ?? null,
      type: clip?.type ?? null,
    };
  });
